import React, { useState, useMemo, useCallback } from "react";
import "./TeamsGroupsList.scss";
import {
  Icon,
  StatusBadge,
  TypeBadge,
  ConfirmModal,
  FormDrawer,
} from "./sharedComponents";

const ROLE_OPTIONS = ["Owner", "Admin", "Member", "Guest"];
const POST_OPTIONS = ["All Members", "Owner/Admin Only", "Selected Members"];
const CHANNEL_TYPES = ["Standard", "Private", "Announcement", "Project"];

// ─── Spinner Icon ────────────────────────────────────────────────────────────
const SpinnerIcon = (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0062FE"
    strokeWidth="2.5"
    strokeLinecap="round"
    style={{ animation: "dpSpin 0.8s linear infinite" }}
  >
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

function TeamsGroupsList({
  teams,
  departments,
  users,
  loading,
  onOpenTeam,
  onCreateTeam,
  onUpdateTeam,
  onRefresh,
}) {
  const [search, setSearch] = useState("");
  const [department, setDepartment] = useState("All");
  const [type, setType] = useState("All");
  const [status, setStatus] = useState("Active");
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const departmentOptions = useMemo(() => {
    const deptList = departments.map((d) => d.name || d.label);
    return ["All", ...deptList];
  }, [departments]);

  const userOptions = useMemo(() => {
    return users.map((u) => ({
      value: u.id,
      label: `${u.fullName} (${u.department?.name || "N/A"})`,
    }));
  }, [users]);

  const filteredTeams = useMemo(() => {
    return teams.filter((t) => {
      if (department !== "All" && t.department !== department) return false;
      if (type !== "All" && t.type !== type) return false;
      if (status !== "All" && t.status !== status) return false;
      if (search && !t.name.toLowerCase().includes(search.toLowerCase()))
        return false;
      return true;
    });
  }, [teams, search, department, type, status]);

  const getCreateTeamFields = useCallback(
    () => [
      {
        name: "name",
        label: "Team Name",
        type: "text",
        placeholder: "e.g. IT Operations",
        required: true,
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        placeholder: "What is this team for?",
        rows: 3,
      },
      {
        name: "row1",
        type: "custom",
        render: ({ form, updateField }) => (
          <div className="form-row">
            <div className="form-group">
              <label>Linked Department</label>
              <select
                value={form.departmentId || ""}
                onChange={(e) => updateField("departmentId", e.target.value)}
              >
                <option value="">Select Department</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Privacy Type</label>
              <select
                value={form.privacyType || "standard"}
                onChange={(e) => updateField("privacyType", e.target.value)}
              >
                <option value="standard">Standard</option>
                <option value="private">Private</option>
              </select>
            </div>
          </div>
        ),
      },
      {
        name: "owner",
        label: "Owner",
        type: "select",
        required: true,
        placeholder: "Select owner",
        options: users.map((u) => ({
          value: u.id,
          label: `${u.fullName} (${u.department?.name || "N/A"})`,
        })),
      },
      {
        name: "initialMembers",
        label: "Initial Members",
        type: "checkbox-group",
        options: users.map((u) => ({
          value: u.id,
          label: u.fullName,
          subtitle: u.department?.name || "N/A",
        })),
        hint: true,
      },
    ],
    [departments, users],
  );

  const handleCreateTeam = useCallback(
    (form) => {
      onCreateTeam(form);
      setShowCreateDrawer(false);
    },
    [onCreateTeam],
  );

  const handleToggleStatus = useCallback(
    (team) => {
      const newStatus = team.status === "Active" ? "Disabled" : "Active";
      setConfirm({
        title: newStatus === "Disabled" ? "Disable team" : "Enable team",
        message:
          newStatus === "Disabled"
            ? `Disable ${team.name}? Members will no longer be able to post or join channels.`
            : `Re-enable ${team.name}? Members will regain access.`,
        confirmLabel: newStatus === "Disabled" ? "Disable" : "Enable",
        danger: newStatus === "Disabled",
        onConfirm: () => {
          onUpdateTeam({ ...team, status: newStatus });
          setConfirm(null);
        },
      });
    },
    [onUpdateTeam],
  );

  return (
    <div className="teams-list-page">
      {/* ── Header - always visible ── */}
      <div className="teams-list-page__header">
        <div>
          <h2>Teams / Groups</h2>
          <p>
            Manage Teams-like communication spaces, channels and permissions.
          </p>
        </div>
        <button
          className="btn btn--primary"
          onClick={() => setShowCreateDrawer(true)}
        >
          <Icon name="plus" size={16} /> Create Team
        </button>
      </div>

      {/* ── Filter bar - always visible ── */}
      <div className="filter-bar">
        <div className="filter-bar__group">
          <label>Department</label>
          <select
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
          >
            {departmentOptions.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-bar__group">
          <label>Type</label>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="All">All</option>
            <option value="Standard">Standard</option>
            <option value="Private">Private</option>
          </select>
        </div>
        <div className="filter-bar__group">
          <label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="All">All</option>
            <option value="Active">Active</option>
            <option value="Disabled">Disabled</option>
          </select>
        </div>
        <div className="filter-bar__search">
          <Icon name="search" size={16} />
          <input
            type="text"
            placeholder="Search team"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* ── Table area - spinner only here when loading ── */}
      <div className="table-wrap table-wrap--card">
        {loading ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "60px 20px",
              minHeight: "300px",
            }}
          >
            {SpinnerIcon}
            <p style={{ marginTop: 12, color: "#6B7280", fontSize: 14 }}>
              Loading teams…
            </p>
            <style>{`
              @keyframes dpSpin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Team Name</th>
                <th>Department</th>
                <th>Owner</th>
                <th>Members</th>
                <th>Channels</th>
                <th>Type</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredTeams.map((team) => (
                <tr
                  key={team.id}
                  className="row-clickable"
                  onClick={() => onOpenTeam(team)}
                >
                  <td className="cell-strong">{team.name}</td>
                  <td>{team.department}</td>
                  <td>{team.owner}</td>
                  <td>{team.membersCount || team.members?.length || 0}</td>
                  <td>{team.channelsCount || team.channels?.length || 0}</td>
                  <td>
                    <TypeBadge type={team.type} />
                  </td>
                  <td>
                    <StatusBadge status={team.status} />
                  </td>
                </tr>
              ))}
              {filteredTeams.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty-row">
                    No teams match the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Create Drawer ── */}
      {showCreateDrawer && (
        <FormDrawer
          open={showCreateDrawer}
          title="Create Team"
          subtitle="Create a new team with members and channels."
          fields={getCreateTeamFields()}
          onSubmit={handleCreateTeam}
          onClose={() => setShowCreateDrawer(false)}
          submitLabel="Create Team"
        />
      )}

      {/* ── Confirm Modal ── */}
      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          danger={confirm.danger}
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

export default TeamsGroupsList;
